import React, { useState } from "react";
import { Loader2, UserPlus } from "lucide-react";
import { Button } from "@pgs-core/ui/components/button";
import { Input } from "@pgs-core/ui/components/input";
import { InviteLinkBox } from "./invite-link-box";
import type { ApiClient } from "@pgs-core/portal-lib/api";
import type { AdminClient } from "../lib/outlet-context";

// Self-contained "add a portal user" form: name + email fields + submit, and
// the one-time invite link it returns. Shared by the full UsersPanel and the
// "Portal user" provision step. Calls onAdded() after a successful create so
// the caller can refresh its own state (user list / client row).
export function AddPortalUserForm({ client, api, onAdded, className = "" }: {
  client: AdminClient;
  api: ApiClient | null;
  onAdded?: () => void;
  className?: string;
}) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");
  const [invite, setInvite] = useState<{ userId: string; email: string; link: string | null } | null>(null);

  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!api || !email.trim()) return;
    setCreating(true);
    setError("");
    setInvite(null);

    try {
      const body = await api.request<{ user: { userId: string; email: string }; inviteLink: string | null }>(
        `/admin/clients/${client.id}/users`,
        {
          method: "POST",
          body: JSON.stringify({ email: email.trim(), display_name: displayName.trim() || null }),
        }
      );
      setInvite({ userId: body.user.userId, email: body.user.email, link: body.inviteLink });
      setEmail("");
      setDisplayName("");
      onAdded?.();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className={className}>
      <form onSubmit={submit} className="flex items-center gap-2">
        <Input
          placeholder="Name (shown on blog posts)"
          value={displayName}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setDisplayName(event.target.value)}
          disabled={creating || !api}
          className="max-w-44"
          aria-label="Display name"
        />
        <Input
          type="email"
          placeholder="owner@business.co.uk"
          value={email}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setEmail(event.target.value)}
          disabled={creating || !api}
          className="max-w-xs"
        />
        <Button type="submit" size="sm" disabled={creating || !email.trim() || !api}>
          {creating ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <UserPlus className="h-3.5 w-3.5" />
          )}
          {creating ? "Adding…" : "Add user"}
        </Button>
      </form>

      {invite && api && (
        <InviteLinkBox
          api={api}
          clientId={client.id}
          invite={invite}
          onLinkChange={(link) => setInvite((current) => (current ? { ...current, link } : current))}
        />
      )}

      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
    </div>
  );
}
